import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpService } from './http.service';
import { environment } from '../../environments/environment';

export interface WeightEstimatorVendor {
  vendor: string;
  total: number;
  missing: number;
  control: number;
}

export interface WeightEstimatorRun {
  _id: string;
  kind: 'backtest' | 'generate';
  vendor: string;
  params: any;
  status: 'running' | 'complete' | 'failed';
  error?: string;
  metrics?: any;
  sample_size?: number;
  gap_total?: number;
  started_at: string;
  finished_at?: string;
  user?: { _id: string; name: string };
}

@Injectable({
  providedIn: 'root'
})
export class WeightEstimatorService {
  // WS events forwarded from web-socket.service: progress / run-complete / run-failed
  public events$ = new Subject<any>();

  constructor(private httpService: HttpService) {}

  getVendors(): Observable<WeightEstimatorVendor[]> {
    return this.httpService.get(environment.API_PATH + '/weight-estimator/vendors');
  }

  getDefaults(): Observable<any> {
    return this.httpService.get(environment.API_PATH + '/weight-estimator/defaults');
  }

  startBacktest(vendor: string, params: any): Observable<{ run_id: string }> {
    return this.httpService.post(environment.API_PATH + '/weight-estimator/backtest', { vendor, params });
  }

  startGenerate(vendor: string, params: any): Observable<{ run_id: string }> {
    return this.httpService.post(environment.API_PATH + '/weight-estimator/generate', { vendor, params });
  }

  getRuns(vendor?: string): Observable<WeightEstimatorRun[]> {
    const query = vendor ? '?vendor=' + encodeURIComponent(vendor) : '';
    return this.httpService.get(environment.API_PATH + '/weight-estimator/runs' + query);
  }

  getRun(id: string): Observable<WeightEstimatorRun> {
    return this.httpService.get(environment.API_PATH + '/weight-estimator/run/' + id);
  }

  getRunResults(id: string, page: number, limit: number, filter: { under?: boolean; tier?: string }): Observable<any> {
    let query = `?page=${page}&limit=${limit}`;
    if (filter.under) query += '&under=true';
    if (filter.tier) query += '&tier=' + encodeURIComponent(filter.tier);
    return this.httpService.get(environment.API_PATH + '/weight-estimator/run/' + id + '/results' + query);
  }

  getEstimates(
    vendor: string, page: number, limit: number,
    filter: { status?: string; tier?: string; minConfidence?: number; sort?: string }
  ): Observable<any> {
    let query = `?vendor=${encodeURIComponent(vendor)}&page=${page}&limit=${limit}`;
    if (filter.status) query += '&status=' + encodeURIComponent(filter.status);
    if (filter.tier) query += '&tier=' + encodeURIComponent(filter.tier);
    if (filter.minConfidence) query += '&minConfidence=' + filter.minConfidence;
    if (filter.sort) query += '&sort=' + encodeURIComponent(filter.sort);
    return this.httpService.get(environment.API_PATH + '/weight-estimator/estimates' + query);
  }

  getPreset(vendor: string): Observable<any> {
    return this.httpService.get(environment.API_PATH + '/weight-estimator/preset?vendor=' + encodeURIComponent(vendor));
  }

  savePreset(vendor: string, params: any): Observable<any> {
    return this.httpService.post(environment.API_PATH + '/weight-estimator/preset', { vendor, params });
  }

  getMisses(vendor: string): Observable<any[]> {
    return this.httpService.get(environment.API_PATH + '/weight-estimator/misses?vendor=' + encodeURIComponent(vendor));
  }

  reviewEstimates(body: { ids?: string[]; action: 'approve' | 'reject'; vendor?: string; filter?: any }): Observable<any> {
    return this.httpService.post(environment.API_PATH + '/weight-estimator/estimates/review', body);
  }

  releaseEstimates(vendor: string): Observable<{ released: number; skipped: number }> {
    return this.httpService.post(environment.API_PATH + '/weight-estimator/estimates/release', { vendor });
  }

  revertEstimates(vendor: string, ids?: string[]): Observable<{ reverted: number; skipped: number }> {
    return this.httpService.post(environment.API_PATH + '/weight-estimator/estimates/revert', { vendor, ids });
  }
}
